import React, { useEffect } from 'react';
import { Modal, Form, Input, Select, Checkbox, InputNumber, Alert, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { inventoryAttributeDefinitionService } from '@services/admin/inventoryAttributeDefinitionService';

export default function DefinitionForm({ visible, onCancel, onSuccess, record }) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const isEdit = !!record;
  // 監看 data_type，切到 boolean 時主動把可能殘留的 required 髒值清掉，
  // 避免使用者先勾了 required 再切到 boolean 仍帶 true 送出（Task 2 後端 schema 已禁此組合）。
  const dataType = Form.useWatch('data_type', form);

  useEffect(() => {
    if (visible) {
      if (record) {
        form.setFieldsValue(record);
      } else {
        form.resetFields();
      }
    }
  }, [visible, record, form]);

  useEffect(() => {
    if (dataType === 'boolean' && form.getFieldValue('required')) {
      form.setFieldValue('required', false);
    }
  }, [dataType, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const result = isEdit
        ? await inventoryAttributeDefinitionService.update(record.id, values)
        : await inventoryAttributeDefinitionService.create(values);

      if (result.success) {
        message.success(t('common.submit.success'));
        onSuccess();
      } else {
        message.error(result.message || t('common.error.operation_failed'));
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <Modal
      title={isEdit ? t('shared.inventory_attribute_definition.form.edit_title') : t('shared.inventory_attribute_definition.form.new_title')}
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      okText={t('common.button.confirm')}
      cancelText={t('common.button.cancel')}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label={t('shared.inventory_attribute_definition.fields.name')}
          rules={[
            { required: true, message: t('common.validation.required') },
            { pattern: /^[a-z0-9_]+$/, message: t('shared.inventory_attribute_definition.errors.name_pattern') },
          ]}
        >
          <Input placeholder={t('shared.inventory_attribute_definition.form.name_placeholder')} disabled={isEdit} />
        </Form.Item>
        <Form.Item
          name="label"
          label={t('shared.inventory_attribute_definition.fields.label')}
          rules={[{ required: true, message: t('common.validation.required') }]}
        >
          <Input placeholder={t('shared.inventory_attribute_definition.form.label_placeholder')} />
        </Form.Item>
        <Form.Item
          name="data_type"
          label={t('shared.inventory_attribute_definition.fields.data_type')}
          rules={[{ required: true, message: t('common.validation.required') }]}
        >
          <Select placeholder={t('common.placeholder.please_select')}>
            <Select.Option value="text">{t('shared.inventory_attribute_definition.data_types.text')}</Select.Option>
            <Select.Option value="number">{t('shared.inventory_attribute_definition.data_types.number')}</Select.Option>
            <Select.Option value="date">{t('shared.inventory_attribute_definition.data_types.date')}</Select.Option>
            <Select.Option value="boolean">{t('shared.inventory_attribute_definition.data_types.boolean')}</Select.Option>
            <Select.Option value="select">{t('shared.inventory_attribute_definition.data_types.select')}</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prev, current) => prev.data_type !== current.data_type}
        >
          {({ getFieldValue }) =>
            getFieldValue('data_type') === 'select' ? (
              <Form.Item
                name="options"
                label={t('shared.inventory_attribute_definition.fields.options')}
                rules={[{ required: true, message: t('common.validation.required') }]}
              >
                <Select mode="tags" placeholder={t('shared.inventory_attribute_definition.form.options_placeholder')} />
              </Form.Item>
            ) : null
          }
        </Form.Item>

        {dataType === 'boolean' ? (
          <Alert
            type="info"
            showIcon
            message={t('shared.inventory_attribute_definition.form.boolean_vs_select_hint')}
            style={{ marginBottom: 16 }}
          />
        ) : (
          <Form.Item name="required" valuePropName="checked">
            <Checkbox>{t('shared.inventory_attribute_definition.fields.required')}</Checkbox>
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}